import React, { useCallback, useEffect, useState } from 'react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useRealtime } from '../lib/useRealtime';
import { Split, Plus, Edit3, Zap, Check } from 'lucide-react';
import { Modal } from '../lib/Modal';
import { WaiterOrderEditor } from '../components/WaiterOrderEditor';
import { OrderItemsList } from '../components/OrderItemsList';
import { sortTablesByNumber } from '../lib/tableSort';
import { hasPlanFeature } from '../lib/plans';
import { PlanUpgradeNotice } from '../components/PlanUpgradeNotice';
import { STATUS_LABEL, WAITER_FLOW, NEXT_LABEL, extraCount, hasExtra, hasPendingExtra, readyToServeCount, readyToServeIndexes } from '../lib/orderFlow';
import { ReadyToServeSection } from '../components/ReadyToServeSection';

export default function OrdersPage() {
    const { user, restaurant } = useAuth();
    const [orders, setOrders] = useState([]);
    const [filter, setFilter] = useState('ACTIVE');
    const [splitFor, setSplitFor] = useState(null);
    const [splitParts, setSplitParts] = useState(2);
    const [splitResult, setSplitResult] = useState(null);
    const [tables, setTables] = useState([]);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editingOrder, setEditingOrder] = useState(null);
    const isWaiter = user?.role === 'waiter';
    // Manager/admin can void an order that's already in the kitchen (waiters can't) —
    // the only clean way to clear a stuck/wrong ticket off the board.
    const isManager = ['restaurant_admin', 'manager', 'super_admin', 'platform_staff'].includes(user?.role);

    const reload = useCallback(() => {
        const reqs = [api.get('/r/orders')];
        if (isWaiter) reqs.push(api.get('/r/tables'));
        Promise.all(reqs).then(([ordersRes, tablesRes]) => {
            setOrders(ordersRes.data.items);
            if (tablesRes) setTables(sortTablesByNumber(tablesRes.data.items || []));
        });
    }, [isWaiter]);

    useEffect(() => { reload(); }, [reload]);

    useRealtime(user?.restaurant_id, (msg) => {
        if (msg.type === 'new_order' || msg.type === 'order_updated') reload();
    });

    const updateStatus = async (id, status) => {
        await api.put(`/r/orders/${id}/status`, { status });
        reload();
    };

    const cancelOrder = async (id) => {
        if (!window.confirm('Anulezi această comandă? Eliberează masa și nu poate fi reluată.')) return;
        try { await api.put(`/r/orders/${id}/status`, { status: 'CANCELLED' }); reload(); }
        catch (e) { alert(formatApiError(e)); }
    };

    const advanceExtra = async (id, action) => {
        try {
            await api.post(`/r/orders/${id}/extra`, { action });
            reload();
        } catch (e) { alert(formatApiError(e)); }
    };

    const serveReady = async (id) => {
        try { await api.post(`/r/orders/${id}/serve-item`, { group: 'all' }); reload(); }
        catch (e) { alert(formatApiError(e)); }
    };

    const openSplit = (order) => {
        setSplitFor(order); setSplitParts(2); setSplitResult(null);
    };

    const doSplit = async () => {
        const { data } = await api.post(`/r/orders/${splitFor.id}/split`, { parts: splitParts });
        setSplitResult(data);
        reload();
    };

    const filtered = orders.filter(o => {
        if (filter === 'ALL') return true;
        if (filter === 'ACTIVE') return !['PAID', 'CANCELLED'].includes(o.status);
        return o.status === filter;
    });

    if (!hasPlanFeature(restaurant, 'orders_realtime')) {
        return (
            <div className="fade-in-up" data-testid="orders-page">
                <div className="kicker">Comenzi</div>
                <h1 className="headline" style={{ fontSize: '2.4rem', margin: '0.3rem 0 1.4rem' }}>Comenzi online live.</h1>
                <PlanUpgradeNotice featureLabel="Comenzile online de la masă" />
            </div>
        );
    }

    return (
        <div className="fade-in-up" data-testid="orders-page">
            <div className="kicker">Comenzi</div>
            <h1 className="headline" style={{ fontSize: '2.4rem', margin: '0.3rem 0 1rem' }}>Fluxul de comenzi.</h1>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: '1.2rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {['ACTIVE', 'PENDING', 'IN_KITCHEN', 'READY', 'PAID', 'ALL'].map(f => (
                        <button key={f} onClick={() => setFilter(f)} className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-secondary'}`} data-testid={`filter-${f}`}>
                            {STATUS_LABEL[f] || (f === 'ACTIVE' ? 'Active' : f === 'ALL' ? 'Toate' : f)}
                        </button>
                    ))}
                </div>
                {isWaiter && (
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={!tables.length}
                        onClick={() => { setEditingOrder(null); setEditorOpen(true); }}
                        data-testid="orders-waiter-new"
                    >
                        <Plus size={14} /> Comandă nouă
                    </button>
                )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                {filtered.map(o => (
                    <div key={o.id} className="panel" data-testid={`order-${o.id}`}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem', flexWrap: 'wrap', gap: 8 }}>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>Masa {o.table_number} <span style={{ color: 'var(--text-faint)', fontWeight: 400, fontSize: '0.85rem' }}>#{o.id.slice(0, 6)}</span></div>
                                <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>{new Date(o.created_at).toLocaleString('ro-RO')}</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                {hasExtra(o, 'new') && (
                                    <span className="badge badge-magenta" data-testid={`order-${o.id}-extra-badge`}>
                                        <Zap size={12} style={{ marginRight: 3, verticalAlign: '-1px' }} /> EXTRA NOU ({extraCount(o, 'new')})
                                    </span>
                                )}
                                {(hasExtra(o, 'sent') || hasExtra(o, 'cooking')) && <span className="badge badge-cyan">Extra la bucătărie</span>}
                                {hasExtra(o, 'ready') && <span className="badge badge-amber">Extra gata de servit</span>}
                                <span className={`status-chip status-${o.status}`}>{STATUS_LABEL[o.status]}</span>
                                <div className="headline" style={{ fontSize: '1.5rem', color: 'var(--amber)' }}>{o.total.toFixed(2)} RON</div>
                            </div>
                        </div>
                        <div style={{ marginBottom: '0.7rem' }}>
                            <OrderItemsList items={o.items} showPrices />
                        </div>
                        <ReadyToServeSection order={o} />
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {isWaiter && !['PAID', 'CANCELLED'].includes(o.status) && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => { setEditingOrder(o); setEditorOpen(true); }}
                                    data-testid={`order-${o.id}-edit`}
                                >
                                    <Edit3 size={14} /> Adaugă / Editează
                                </button>
                            )}
                            {hasExtra(o, 'new') && (
                                <button className="btn btn-sm btn-primary" onClick={() => advanceExtra(o.id, 'confirm')}
                                        data-testid={`order-${o.id}-extra-confirm`}>
                                    <Zap size={14} style={{ marginRight: 4 }} /> Confirmă extra → bucătărie
                                </button>
                            )}
                            {/* ONE serve button: regular ready items + ready extras together. */}
                            {(() => {
                                const readyN = readyToServeCount(o, 'all') + extraCount(o, 'ready');
                                if (readyN > 0) {
                                    return (
                                        <button className="btn btn-sm btn-primary" style={{ background: 'var(--neon-teal)' }}
                                                onClick={() => serveReady(o.id)}
                                                data-testid={`order-${o.id}-serve`}>
                                            <Check size={14} style={{ marginRight: 4 }} /> Servește ({readyN})
                                        </button>
                                    );
                                }
                                if (o.status === 'READY' && readyToServeIndexes(o, 'all').length === 0) {
                                    return (
                                        <button className="btn btn-sm btn-primary" style={{ background: 'var(--neon-teal)' }}
                                                onClick={() => updateStatus(o.id, 'SERVED')}
                                                data-testid={`order-${o.id}-serve-all`}>
                                            <Check size={14} style={{ marginRight: 4 }} /> Servește toate
                                        </button>
                                    );
                                }
                                return null;
                            })()}
                            {(WAITER_FLOW[o.status] || [])
                                .filter(next => !(next === 'PAID' && hasPendingExtra(o)))
                                .map(next => (
                                <button key={next} className={`btn btn-sm ${next === 'CANCELLED' ? 'btn-danger' : 'btn-primary'}`}
                                        onClick={() => updateStatus(o.id, next)}
                                        data-testid={`order-${o.id}-to-${next}`}>
                                    {NEXT_LABEL[next] || next}
                                </button>
                            ))}
                            {o.status === 'SERVED' && hasPendingExtra(o) && (
                                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', alignSelf: 'center' }}>
                                    Rezolvă extra-ul înainte de încasare
                                </span>
                            )}
                            {['SERVED', 'PAID'].includes(o.status) && o.items.length > 1 && hasPlanFeature(restaurant, 'split_bill') && (
                                <button className="btn btn-sm btn-secondary" onClick={() => openSplit(o)} data-testid={`order-${o.id}-split`}>
                                    <Split size={14} style={{ marginRight: 4 }} /> Împarte nota
                                </button>
                            )}
                            {/* Manager/admin-only escape hatch: void an order already in the kitchen. */}
                            {isManager && ['IN_KITCHEN', 'READY', 'SERVED'].includes(o.status) && (
                                <button className="btn btn-sm btn-danger" onClick={() => cancelOrder(o.id)}
                                        data-testid={`order-${o.id}-manager-cancel`}>
                                    Anulează comanda
                                </button>
                            )}
                        </div>
                    </div>
                ))}
                {!filtered.length && (
                    <div className="panel" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Nicio comandă în această secțiune.</div>
                )}
            </div>

            <Modal open={!!splitFor} onClose={() => setSplitFor(null)} title={splitFor ? `Împarte nota — Masa ${splitFor.table_number}` : ''} maxWidth={420}>
                {splitFor && (
                    <div data-testid="split-modal">
                        <div style={{ marginBottom: '1rem' }}>
                            <div className="kicker">Total comandă</div>
                            <div className="headline" style={{ fontSize: '2rem', color: 'var(--amber)' }}>{splitFor.total.toFixed(2)} RON</div>
                        </div>
                        <label className="label">Împarte în câte părți egale?</label>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: '1rem' }}>
                            {[2,3,4,5,6].map(n => (
                                <button key={n} type="button" className={`btn btn-sm ${splitParts === n ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setSplitParts(n)} data-testid={`split-parts-${n}`}>{n}</button>
                            ))}
                        </div>
                        {!splitResult && (
                            <button type="button" className="btn btn-primary" style={{ width: '100%' }} onClick={doSplit} data-testid="split-do">Calculează</button>
                        )}
                        {splitResult && (
                            <div data-testid="split-result">
                                <div className="kicker" style={{ marginBottom: 8 }}>Rezultat</div>
                                {splitResult.parts.map(p => (
                                    <div key={p.part} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0.7rem', background: 'var(--surface-2)', borderRadius: 8, marginBottom: 6 }}>
                                        <span>Persoană {p.part}</span>
                                        <strong style={{ color: 'var(--amber)' }}>{p.amount.toFixed(2)} RON</strong>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            {isWaiter && (
                <WaiterOrderEditor
                    open={editorOpen}
                    order={editingOrder}
                    tables={tables}
                    activeOrders={orders.filter((o) => !['PAID', 'CANCELLED'].includes(o.status))}
                    onClose={() => { setEditorOpen(false); setEditingOrder(null); }}
                    onSaved={reload}
                />
            )}
        </div>
    );
}
